// TalentoHumano.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import './TalentoHumano.css';
import talentoHumanoImg from '../assets/thumano.avif';
import { 
  Search, 
  GraduationCap, 
  BarChart3, 
  Briefcase, 
  Heart, 
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Menu,
  X,
  Home,
  CalendarDays,
  FileCheck,
  NotebookText,
  Receipt,
  Shirt,
  BookUser,
  Download,
  ExternalLink
} from 'lucide-react';
import alejandraGalindo from '../assets/alejandra galindo.png';
import danielLeal from '../assets/daniel leal.png';
import indiraSolano from '../assets/indira solano.png';
import marialeBarreto from '../assets/mariale barreto.png';
import orianaVidal from '../assets/oriana vidal.jpg';
import yudithGonzalez from '../assets/yudith Gonzalez.png';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Datos de slides para el hero
const slides = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&h=900&fit=crop&auto=format",
    tag: "Captaciones",
    tagIcon: Search,
    title: "Atrae y selecciona\nel mejor talento\npara tu organización",
    subtitle: "Gestiona todo el proceso de captación: publicación de vacantes, recepción de postulaciones, entrevistas y vinculación desde un solo lugar.",
    cta: "Publicar vacante",
    ctaSecondary: "Ver candidatos",
    stat: { value: "47", label: "Vacantes activas" },
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1600&h=900&fit=crop&auto=format",
    tag: "Vacaciones",
    tagIcon: CalendarDays,
    title: "Gestiona y planifica\ntus períodos de\ndescanso fácilmente",
    subtitle: "Solicita, aprueba y controla los días de disfrute de vacaciones de cada colaborador con visibilidad en tiempo real del saldo disponible.",
    cta: "Solicitar vacaciones",
    ctaSecondary: "Ver mi saldo",
    stat: { value: "18", label: "Días disponibles" },
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&h=900&fit=crop&auto=format",
    tag: "Solicitud de Recaudos",
    tagIcon: FileCheck,
    title: "Solicita tus documentos\nlaborales de forma\nrápida y digital",
    subtitle: "Certificados laborales, constancias de trabajo, cartas de referencia y más. Solicita, rastrea y recibe tus documentos en línea sin filas.",
    cta: "Solicitar documento",
    ctaSecondary: "Mis solicitudes",
    stat: { value: "24h", label: "Tiempo de respuesta" },
  },
];

// Datos de accesos rápidos para el banner
const quickAccessItems = [
  {
    icon: NotebookText,
    label: "Politicas y Normas",
    description: "Reglamento",
    path: 'https://drive.google.com/file/d/1mz7YWZjQUaduyGGFdhncPwzzdsCFWeYs/preview',
    downloadUrl: 'https://drive.google.com/uc?export=download&id=1mz7YWZjQUaduyGGFdhncPwzzdsCFWeYs',
    color: "#0068b3",
  },
  {
    icon: Receipt,
    label: "Prestaciones Sociales",
    description: "Anticipos",
    path: 'https://drive.google.com/file/d/1joIOaiiGs-E__c-aKrDmBr2tTP5QNnvw/preview',
    downloadUrl: 'https://drive.google.com/uc?export=download&id=1joIOaiiGs-E__c-aKrDmBr2tTP5QNnvw',
    color: "#00a4e5",
  },
  {
    icon: FileCheck,
    label: "Constacias de Trabajo",
    description: "Documentos laborales",
    path: 'https://drive.google.com/file/d/1E_dNHQJg71z98kBj-zBJUul7cLcjNn5F/preview',
    downloadUrl: 'https://drive.google.com/uc?export=download&id=1E_dNHQJg71z98kBj-zBJUul7cLcjNn5F',
    color: "#00ba52",
  },
  {
    icon: Shirt,
    label: "Codigo de Vestimenta",
    description: "Reglamento",
    path: 'https://drive.google.com/file/d/1HzGvsWd25spiH18fW99P6VTGffXaL6nl/preview',
    downloadUrl: 'https://drive.google.com/uc?export=download&id=1HzGvsWd25spiH18fW99P6VTGffXaL6nl',
    color: "#06b6d4",
  },
  {
    icon: BookUser,
    label: "Directorio",
    description: "Corporativo",
    path: 'https://docs.google.com/spreadsheets/d/1_z8FuG5AIjBnHxDTdEQS8J3tsvaDyMn1/preview',
    downloadUrl: 'https://docs.google.com/spreadsheets/d/1_z8FuG5AIjBnHxDTdEQS8J3tsvaDyMn1/export?format=xlsx',
    color: "#10b98b",
  },
];

// Datos de módulos
const modules = [
  {
    icon: Search,
    title: "Captación",
    description: "Pipeline completo de selección: publicación de vacantes, seguimiento de candidatos, entrevistas y onboarding digital.",
    items: ["Publicación multicanal", "ATS integrado", "Evaluaciones en línea", "Onboarding digital"],
    color: "#0068b3",
    path: '/reclutamiento',
  },
  {
    icon: GraduationCap,
    title: "Capacitación",
    description: "LMS corporativo con cursos, talleres presenciales, certificaciones y seguimiento de avance por colaborador.",
    items: ["Catálogo de cursos", "Rutas de aprendizaje", "Certificaciones", "Seguimiento de progreso"],
    color: "#00a4e5",
    path: '/talento-humano/capacitacion',
  },
  {
    icon: BarChart3,
    title: "Evaluación de Desempeño",
    description: "Evaluaciones 360°, gestión de OKRs, feedback estructurado y reportes de desempeño para toma de decisiones.",
    items: ["Evaluación 360°", "Gestión OKR", "Feedback continuo", "Análisis de brechas"],
    color: "#00ba52",
    path: '/talento-humano/evaluaciones',
  },
  {
    icon: Briefcase,
    title: "Gestión Administrativa",
    description: "Control de nómina, vacaciones, permisos, documentos y expediente digital de cada colaborador centralizado.",
    items: ["Control de asistencia", "Gestión de permisos", "Expediente digital", "Firma electrónica"],
    color: "#0068b3",
    path: '/talento-humano/administracion',
  },
  {
    icon: Heart,
    title: "Bienestar & Clima",
    description: "Encuestas de clima organizacional, programas de bienestar, reconocimientos y comunicación interna efectiva.",
    items: ["Encuestas de clima", "Programa de beneficios", "Reconocimientos", "Canal de comunicación"],
    color: "#00a4e5",
    path: '/talento-humano/bienestar',
  },
  {
    icon: TrendingUp,
    title: "Análisis & Reportes",
    description: "Dashboards ejecutivos, indicadores de RRHH, análisis predictivo de rotación y reportes regulatorios automáticos.",
    items: ["Dashboard ejecutivo", "KPIs de RRHH", "Análisis predictivo", "Reportes regulatorios"],
    color: "#00ba52",
    path: '/talento-humano/reportes',
  },
];

// Datos del equipo
const teamHighlights = [
  {
    name: "Alejandra Galindo",
    role: "Gerente de Talento Humano",
    img: alejandraGalindo,
  },
  {
    name: "Indira Solano",
    role: "Coordinadora de Captación y Desarrollo",
    img: indiraSolano,
  },
  {
    name: "Oriana Vidal",
    role: "Coordinadora de Compensación y Beneficios",
    img: orianaVidal,
  },
  {
    name: "Yudith Gonzalez",
    role: "Coordinadora Integral de TH",
    img: yudithGonzalez,
  },
  {
    name: "Daniel Leal",
    role: "Especialista de Captación y Desarrollo",
    img: danielLeal,
  },
  {
    name: "Maria Alejandra Barreto",
    role: "Especialista de Compensación y Beneficios",
    img: marialeBarreto,
  },
];

export default function TalentoHumano() {
  const { hasAccess } = useAuth();
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [activeTab, setActiveTab] = useState("Inicio");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const quickAccessRef = useRef(null);

  // Verificar permisos
  const canAccessReclutamiento = hasAccess('/reclutamiento');
  const canAccessCapacitacion = hasAccess('/talento-humano/capacitacion');
  const canAccessEvaluaciones = hasAccess('/talento-humano/evaluaciones');
  const canAccessAdministracion = hasAccess('/talento-humano/administracion');
  const canAccessBienestar = hasAccess('/talento-humano/bienestar');
  const canAccessReportes = hasAccess('/talento-humano/reportes');

  // Filtrar módulos según permisos
  const visibleModules = modules.filter(mod => {
    switch(mod.title) {
      case "Captación": return canAccessReclutamiento;
      case "Capacitación": return canAccessCapacitacion;
      case "Evaluación de Desempeño": return canAccessEvaluaciones;
      case "Gestión Administrativa": return canAccessAdministracion;
      case "Bienestar & Clima": return canAccessBienestar;
      case "Análisis & Reportes": return canAccessReportes;
      default: return false;
    }
  });

  // Filtrar accesos rápidos según permisos
  const visibleQuickAccess = quickAccessItems.filter(item => {
    return hasAccess(item.path);
  });

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(nextSlide, 5500);
    return () => clearInterval(interval);
  }, [isPlaying, nextSlide]);

  // Scroll del banner de accesos rápidos
  const scrollQuickAccess = (direction) => {
    const container = quickAccessRef.current;
    if (container) {
      const scrollAmount = 280;
      const newScroll = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      container.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    
    }
  };

  // Navegación por tabs
  const tabs = [
    { label: "Inicio", path: '/talentohumano', icon: Home },
    { label: "Captación", path: '/reclutamiento', icon: Search },
    { label: "Capacitación", path: '/talento-humano/capacitacion', icon: GraduationCap },
    { label: "Evaluaciones", path: '/talento-humano/evaluaciones', icon: BarChart3 },
    { label: "Bienestar", path: '/talento-humano/bienestar', icon: Heart },
  ];

  const visibleTabs = tabs.filter(tab => {
    if (tab.label === "Inicio") return true;
    return hasAccess(tab.path);
  });

  const slide = slides[currentSlide];
  const TagIcon = slide.tagIcon;

  const handleTabClick = (tab) => {
    setActiveTab(tab.label);
    navigate(tab.path);
    setMobileMenuOpen(false);
  };

  const handleDocumentClick = (item) => {
    setSelectedDocument(item);
  };

  const closeDocumentModal = () => {
    setSelectedDocument(null);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6 }}
      className="th-container"
    >
      {/* SUBHEADER — navegación interna del módulo */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.3, delay: 0.1 }}
        className="th-subheader"
      >
        <div className="th-subheader-inner">
          {/* Breadcrumb - visible solo en desktop */}
          <div className="th-breadcrumb th-breadcrumb-desktop">
            <span>Portal</span>
            <span className="th-breadcrumb-separator">›</span>
            <span className="th-breadcrumb-current">Talento Humano</span>
          </div>

          {/* Subheader row */}
          <div className="th-subheader-row">
            {/* Módulo label */}
            <div className="th-module-label">
              <div className="th-logo">TH</div>
              <span className="th-module-title">Talento Humano</span>
              
              {/* Botón menú móvil */}
              <button 
                className="th-mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Abrir menú"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              <div className="th-divider-vertical th-divider-desktop" />
              
              {/* Nav tabs - desktop */}
              <div className="th-nav-tabs th-nav-tabs-desktop">
                {visibleTabs.map((tab) => {
                  const isActive = activeTab === tab.label;
                  return (
                    <button
                      key={tab.label}
                      onClick={() => handleTabClick(tab)}
                      className={`th-tab ${isActive ? 'active' : ''}`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Menú móvil desplegable */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="th-mobile-menu"
            >
              <div className="th-mobile-menu-inner">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.label;
                  return (
                    <button
                      key={tab.label}
                      onClick={() => handleTabClick(tab)}
                      className={`th-mobile-menu-item ${isActive ? 'active' : ''}`}
                    >
                      <Icon size={20} />
                      <span>{tab.label}</span>
                      {isActive && <div className="th-mobile-menu-indicator" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* HERO SLIDER */}
      <motion.section 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        transition={{ duration: 0.5, delay: 0.2 }}
        className="th-hero"
      >
        {/* Slides */}
        <div 
          className="th-slider-track"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((s) => (
            <div key={s.id} className="th-slide">
              <img src={s.image} alt={s.title} className="th-slide-image" />
              <div className="th-slide-overlay" />
            </div>
          ))}
        </div>

        {/* Hero content */}
        <div className="th-hero-content">
          {/* Tag */}
          <div className="th-hero-tag">
            <span className="th-tag-line" />
            <span className="th-section-tag">
              <TagIcon size={14} style={{ display: 'inline', marginRight: '6px' }} />
              {slide.tag}
            </span>
            <span className="th-tag-line" />
          </div>

          {/* Título */}
          <h1 className="th-hero-title">{slide.title}</h1>

          {/* Subtítulo */}
          <p className="th-hero-subtitle">{slide.subtitle}</p>

          {/* Stat */}
          <div className="th-hero-stat">
            <span className="th-stat-value">{slide.stat.value}</span>
            <span className="th-stat-label">{slide.stat.label}</span>
          </div>

          {/* Botones */}
          <div className="th-hero-buttons">
            <button className="th-btn-underline">{slide.cta}</button>
            <span className="th-divider-horizontal" />
            <button className="th-btn-underline-accent">{slide.ctaSecondary}</button>
          </div>
        </div>

        {/* Slider controls */}
        <div className="th-slider-controls">
          {/* Indicators */}
          <div className="th-indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`th-indicator ${i === currentSlide ? "active" : ""}`}
                onClick={() => setCurrentSlide(i)}
                aria-label={`Ir al slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Prev / Next */}
          <div className="th-nav-buttons">
            <button 
              onClick={prevSlide}
              className="th-nav-btn"
              aria-label="Slide anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              onClick={nextSlide}
              className="th-nav-btn"
              aria-label="Siguiente slide"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Play/Pause */}
          <button 
            onClick={() => setIsPlaying(!isPlaying)}
            className="th-play-pause"
          >
            {isPlaying ? <Pause size={12} /> : <Play size={12} />}
            {isPlaying ? "Pausar" : "Reproducir"}
          </button>
        </div>

        {/* Slide counter */}
        <div className="th-slide-counter">
          <span className="th-counter-current">
            {String(currentSlide + 1).padStart(2, "0")}
          </span>{" "}
          / {String(slides.length).padStart(2, "0")}
        </div>
      </motion.section>

      {/* QUICK ACCESS BANNER */}
      {visibleQuickAccess.length > 0 && (
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4, delay: 0.25 }}
          className="th-quick-access-section"
        >
          <div className="th-quick-access-header">
            <h3 className="th-quick-access-title">Documentos</h3>
            <div className="th-quick-access-controls">
              <button 
                className="th-quick-access-nav"
                onClick={() => scrollQuickAccess('left')}
                aria-label="Scroll izquierda"
              >
                <ChevronLeft size={20} />
              </button>
              <button 
                className="th-quick-access-nav"
                onClick={() => scrollQuickAccess('right')}
                aria-label="Scroll derecha"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div 
            className="th-quick-access-track"
            ref={quickAccessRef}
          >
            {visibleQuickAccess.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.button
                  key={i}
                  className="th-quick-access-item"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleDocumentClick(item)}
                  style={{
                    '--item-color': item.color,
                  }}
                >
                  <div className="th-quick-access-icon">
                    <Icon size={24} />
                  </div>
                  <div className="th-quick-access-info">
                    <span className="th-quick-access-label">{item.label}</span>
                    <span className="th-quick-access-description">{item.description}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.section>
      )}

      {/* DOCUMENT MODAL */}
      <AnimatePresence>
        {selectedDocument && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="th-document-modal-overlay"
            onClick={closeDocumentModal}
          >
            <motion.div 
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="th-document-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="th-document-modal-header">
                <div className="th-document-modal-title">
                  <selectedDocument.icon size={20} />
                  <h3>{selectedDocument.label}</h3>
                </div>
                <div className="th-document-modal-actions">
                  <a 
                    href={selectedDocument.downloadUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="th-document-download-btn"
                  >
                    <Download size={16} />
                    Descargar
                  </a>
                  <a 
                    href={selectedDocument.path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="th-document-external-btn"
                  >
                    <ExternalLink size={16} />
                    Abrir
                  </a>
                  <button 
                    className="th-document-close-btn"
                    onClick={closeDocumentModal}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="th-document-modal-body">
                <iframe 
                  src={selectedDocument.path}
                  title={selectedDocument.label}
                  className="th-document-iframe"
                  allow="autoplay"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODULES SECTION */}
      {visibleModules.length > 0 && (
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4, delay: 0.3 }}
          className="th-modules-section"
          style={{
            width: '100%',
            backgroundImage: `url(${talentoHumanoImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
          }}
        >
          {/* Overlay para mejorar legibilidad */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            zIndex: 1,
          }} />

          {/* Contenido */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div className="th-section-header">
              <span className="th-section-tag">Bienvenidos a Talento Humano Dimassi</span>
              <h2 className="th-section-title">
                Todo lo que necesitas para gestionar el talento
              </h2>
              <div className="th-accent-line" />
              <p className="th-section-description">
                Seis módulos integrados que cubren cada etapa del ciclo de vida del colaborador en tu organización.
              </p>
            </div>

            <div className="th-modules-grid">
              {visibleModules.map((mod, i) => {
                const Icon = mod.icon;
                return (
                  <motion.div 
                    key={mod.title}
                    className="th-module-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    onClick={() => navigate(mod.path)}
                  >
                    <div className="th-module-card-header">
                      <div 
                        className="th-module-icon"
                        style={{
                          background: `linear-gradient(135deg, ${mod.color}33, ${mod.color}11)`,
                          border: `1px solid ${mod.color}44`,
                        }}
                      >
                        <Icon size={22} />
                      </div>
                      <h3 className="th-module-card-title">{mod.title}</h3>
                    </div>
                    
                    <p className="th-module-card-description">{mod.description}</p>
                    
                    <ul className="th-module-items">
                      {mod.items.map((item, j) => (
                        <li key={j} className="th-module-item">
                          <span className="th-module-item-dot">◆</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    <button className="th-module-btn">Acceder al módulo</button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>
      )}

      {/* TEAM SECTION */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.4, delay: 0.4 }}
        className="th-events-team-section"
      >
        <div>
          <span className="th-section-tag">Nuestro equipo</span>
          <h2 className="th-section-title">Equipo de RRHH</h2>
          <div className="th-accent-line" />

          <div className="th-team-grid">
            {teamHighlights.map((person, i) => (
              <motion.div 
                key={i}
                className="th-team-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <img
                  src={person.img}
                  alt={person.name}
                  className="th-team-avatar"
                />
                <div className="th-team-info">
                  <div className="th-team-name">{person.name}</div>
                  <div className="th-team-role">{person.role}</div>
                </div>
                <button className="th-team-contact-btn">Contactar</button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    </motion.div>
  );
}